import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { RHFAutocomplete, RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import UniqueTextField from 'src/components/unique-text-field/UniqueTextField';
import ListboxComponent from 'src/components/virtualized-autocomplete/autocomplete-virtualized';
import Iconify from 'src/components/iconify';
import { Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

export default function PassportNewEditForm({
  citiesData,
  switchState,
  onEmptyPassport,
  onDuplicateStatusChange,
  currentEmp,
  duplicateFields = {},
}) {
  const { control, setValue, watch, resetField, trigger } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'empPassport',
  });

  const handleAdd = () => {
    append({
      _uid: `pass_${Date.now()}_${fields.length}`,
      passSrno: 0,
      passNo: '',
      issuePlace: null,
      issueDt: null,
      expiryDt: null,
      ecnrStamp: false,
    });
  };

  const handleRemove = (index) => {
    const item = fields[index];
    const rowKey = item?._uid || item?.id || `idx_${index}`;
    if (onDuplicateStatusChange) {
      onDuplicateStatusChange(`passport-${rowKey}`)(false);
      if (item?.id) {
        onDuplicateStatusChange(`passport-${item.id}`)(false);
      }
      onDuplicateStatusChange(`passport-${index}`)(false);
    }
    remove(index);
  };

  // Only call onEmptyPassport() after mount and update, not on initial render to prevent inconsistency
  const hasMounted = useRef(false);

  useEffect(() => {
    if (hasMounted.current && fields.length === 0) {
      setTimeout(() => {
        onEmptyPassport?.();
      }, 0);
    }
    hasMounted.current = true;
  }, [fields.length, onEmptyPassport]);


  return (
    <>
      <Box
        mt={4}
        rowGap={3}
        columnGap={2}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
        }}
        mb={3}
      >
        {/* <Typography variant="subtitle2" sx={{ mb: '10px' }}>
          Passport Details:
        </Typography> */}
        <Box>
          <Typography variant="h6" sx={{ color: 'text.disabled', mb: 3 }}>
            Passport Details:
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-end', md: 'center' }}
          justifyContent="flex-end"
        >
          <Button
            size="small"
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={handleAdd}
            sx={{ flexShrink: 0, minWidth: '140px', justifyContent: 'flex-start' }}
          >
            Add Passport
          </Button>
        </Stack>
      </Box>
      <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />} spacing={3}>
        {fields.map((item, index) => (
          <Stack key={item.id} spacing={1.5}>
            <Typography variant="subtitle2" sx={{ color: 'primary', mb: 2 }}>
              Passport {index + 1}:
            </Typography>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <RHFTextField name={`empPassport[${index}].passSrno`} />
            </Box>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Controller
                name={`empPassport[${index}].passNo`}
                control={control}
                rules={{ required: 'Passport Number is required' }}
                render={({ field, fieldState }) => {
                  const rowKey = item._uid || item.id || `idx_${index}`;
                  const isDuplicate = Boolean(
                    duplicateFields[`passport-${rowKey}`] ||
                    duplicateFields[`passport-${item.id}`] ||
                    duplicateFields[`passport-${index}`]
                  );
                  return (
                    <UniqueTextField
                      field={field}
                      error={fieldState.error}
                      isDuplicate={isDuplicate}
                      label={
                        <div>
                          Passport Number<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                        </div>
                      }
                      fieldName="passportNumber"
                      apiEndpoint="/rest/employee/check-duplicate-passport-number"
                      entityId={item.passSrno > 0 ? item.passSrno : null}
                      onDuplicateStatusChange={
                        onDuplicateStatusChange
                          ? onDuplicateStatusChange(`passport-${rowKey}`)
                          : undefined
                      }
                      duplicateMessage="This Passport Number already exists"
                      validationRegex={/^[A-Z0-9]{6,9}$/i}
                      fullWidth
                    />
                  );
                }}
              />
              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`empPassport[${index}].issuePlace`}
                  placeholder="Select Place Of Issue"
                  ListboxComponent={ListboxComponent}
                  label={
                    <div>
                      Place of issue<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  options={citiesData}
                  getOptionLabel={(option) => option.cityName}
                  renderOption={(props, option) => (
                    <li {...props} key={option.cityId}>
                      {option.cityName}
                    </li>
                  )}
                />
              </Stack>
              <Controller
                name={`empPassport[${index}].issueDt`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Date of Issue"
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      trigger(`empPassport[${index}].issueDt`);
                    }}
                    maxDate={new Date()}             
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name={`empPassport[${index}].expiryDt`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Date of Expiry"
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Box sx={{ alignSelf: 'left' }}>
              <RHFCheckbox
                name={`empPassport[${index}].ecnrStamp`}
                label="ECNR Stamped ?"
                labelPlacement="start"
              />
            </Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-end', md: 'center' }}
              justifyContent="flex-end"
            >
              <Button
                size="small"
                color="error"
                startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                onClick={() => handleRemove(index)}
              >
                Remove
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </>
  );
}
PassportNewEditForm.propTypes = {
  citiesData: PropTypes.array,
  switchState: PropTypes.object,
  currentEmp: PropTypes.object,
  onEmptyPassport: PropTypes.func,
  onDuplicateStatusChange: PropTypes.func,
  duplicateFields: PropTypes.object,
};
